import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Users, Shield, ShieldOff, Ban, Check, RefreshCw, Search, Trash2,
  Flag, MessageSquare, Calendar, Video, TrendingUp, Activity, AlertTriangle,
  FileText, ClipboardList,
} from "lucide-react";
import { adminDashboard } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { sfx } from "@/lib/sounds";
import { SkeletonBox, SkeletonList, SkeletonCard } from "@/components/Skeletons";

/* ============================================================
   Shared bits
   ============================================================ */
function StatCard({ label, value, icon: Icon, accent = "text-[#FFBF00]" }) {
  return (
    <div className="rounded-xl glass border border-white/10 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
        {Icon && <Icon size={14} className={accent} />}
      </div>
      <div className={`text-3xl font-bold mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-xl glass border border-white/10 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm uppercase tracking-widest text-white/70">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-center text-white/40 text-sm py-10">{children}</div>;
}

/* ============================================================
   User Manager
   ============================================================ */
export function AdminUserManager() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [banned, setBanned] = useState("");
  const [isCreator, setIsCreator] = useState("");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, sort, limit: 50 };
      if (role) params.role = role;
      if (banned !== "") params.banned = banned === "true";
      if (isCreator !== "") params.is_creator = isCreator === "true";
      const d = await adminDashboard.users(params);
      setUsers(d.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [role, banned, isCreator, sort]);

  const openUser = async (u) => {
    setSelected(u);
    setDetail(null);
    try { setDetail(await adminDashboard.userDetail(u.id)); }
    catch { toast.error("Failed to load user"); }
  };

  const updateUser = async (patch) => {
    if (!selected) return;
    try {
      const updated = await adminDashboard.updateUser(selected.id, patch);
      toast.success("User updated");
      sfx.click();
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      setSelected(updated);
      setDetail((d) => (d ? { ...d, ...updated } : d));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update user");
    }
  };

  const deleteUser = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete ${selected.name}? This cannot be undone.`)) return;
    try {
      await adminDashboard.deleteUser(selected.id);
      toast.success("User deleted");
      setUsers((prev) => prev.filter((u) => u.id !== selected.id));
      setSelected(null); setDetail(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div data-testid="admin-user-manager">
      {/* Filters */}
      <div className="rounded-xl glass border border-white/10 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-user-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search by name, mc username, or email…"
              className="w-full pl-9 pr-3 py-2 rounded-md bg-black/60 border border-white/10 text-sm text-white outline-none focus:border-[#FFBF00]/40"
            />
          </div>
          <select data-testid="admin-user-role" value={role} onChange={(e) => setRole(e.target.value)} className="bg-black/60 border border-white/10 text-white text-sm rounded-md px-3 py-2">
            <option value="">All roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <select data-testid="admin-user-banned" value={banned} onChange={(e) => setBanned(e.target.value)} className="bg-black/60 border border-white/10 text-white text-sm rounded-md px-3 py-2">
            <option value="">Any status</option>
            <option value="true">Banned</option>
            <option value="false">Active</option>
          </select>
          <select data-testid="admin-user-creator" value={isCreator} onChange={(e) => setIsCreator(e.target.value)} className="bg-black/60 border border-white/10 text-white text-sm rounded-md px-3 py-2">
            <option value="">Any type</option>
            <option value="true">Creators</option>
            <option value="false">Non-creators</option>
          </select>
          <select data-testid="admin-user-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-black/60 border border-white/10 text-white text-sm rounded-md px-3 py-2">
            <option value="recent">Recently joined</option>
            <option value="followers">Top followers</option>
            <option value="posts">Top posters</option>
            <option value="last_seen">Recently seen</option>
          </select>
          <button data-testid="admin-user-refresh" onClick={load} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/70 hover:text-white px-3 py-2 rounded-md hover:bg-white/5">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        {/* List */}
        <div className="rounded-xl glass border border-white/10 divide-y divide-white/5 max-h-[65vh] overflow-y-auto" data-testid="admin-user-list">
          {loading && <div className="p-6 text-center text-white/40 text-sm">Loading…</div>}
          {!loading && users.length === 0 && <Empty>No users match.</Empty>}
          {users.map((u) => (
            <button
              key={u.id}
              data-testid={`admin-user-row-${u.id}`}
              onClick={() => openUser(u)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors ${selected?.id === u.id ? "bg-[#FFBF00]/5" : ""}`}
            >
              {u.avatar
                ? <img src={u.avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 bg-white/5" />
                : <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/40 text-xs">{(u.name || "?")[0]}</div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-white">{u.name}</span>
                  {u.role === "admin" && <span className="text-[9px] uppercase tracking-widest text-[#FFBF00] bg-[#FFBF00]/10 px-1.5 py-0.5 rounded">Admin</span>}
                  {u.banned && <span className="text-[9px] uppercase tracking-widest text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">Banned</span>}
                  {u.is_creator && <span className="text-[9px] uppercase tracking-widest text-pink-300 bg-pink-500/10 px-1.5 py-0.5 rounded">Creator</span>}
                  {u.online && <span className="w-2 h-2 rounded-full bg-green-500" title="Online" />}
                </div>
                <div className="text-xs text-white/40 truncate">{u.email || u.mc_username || "—"}</div>
              </div>
              <div className="text-right text-[10px] text-white/40 shrink-0">
                <div>{u.followers_count} followers</div>
                <div>{u.posts_count} posts</div>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="rounded-xl glass border border-white/10 p-4" data-testid="admin-user-detail">
          {!selected && <Empty>Select a user to view details.</Empty>}
          {selected && (
            <>
              <div className="flex items-center gap-3 mb-4">
                {selected.avatar
                  ? <img src={selected.avatar} alt="" className="w-14 h-14 rounded-full object-cover bg-white/5" />
                  : <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center text-white/40">{(selected.name || "?")[0]}</div>}
                <div className="flex-1">
                  <div className="text-lg font-semibold text-white">{selected.name}</div>
                  <div className="text-xs text-white/50">{selected.email || "no email"} · id <code className="text-white/70">{selected.id.slice(0, 8)}</code></div>
                </div>
                <Link
                  to={selected.is_creator ? `/creators/${selected.id}` : `/users/${selected.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs uppercase tracking-widest text-[#FFBF00]/80 hover:text-[#FFBF00]"
                >View →</Link>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-white/70">
                <div>MC: <span className="text-white">{selected.mc_username || "—"}</span></div>
                <div>Tier: <span className="text-white">{selected.creator_tier || "—"}</span></div>
                <div>Followers: <span className="text-white">{selected.followers_count}</span></div>
                <div>Following: <span className="text-white">{selected.following_count}</span></div>
                <div>Posts: <span className="text-white">{selected.posts_count}</span></div>
                <div>Builds: <span className="text-white">{selected.builds_count}</span></div>
                <div>Verified: <span className="text-white">{selected.verified ? "yes" : "no"}</span></div>
                <div>Online: <span className="text-white">{selected.online ? "yes" : "no"}</span></div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="admin-user-toggle-role"
                  onClick={() => updateUser({ role: selected.role === "admin" ? "user" : "admin" })}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white"
                >
                  {selected.role === "admin" ? <><ShieldOff size={12} /> Revoke Admin</> : <><Shield size={12} /> Make Admin</>}
                </button>
                <button
                  data-testid="admin-user-toggle-ban"
                  onClick={() => updateUser({ banned: !selected.banned })}
                  className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md ${selected.banned ? "bg-green-500/10 text-green-300 hover:bg-green-500/20" : "bg-red-500/10 text-red-300 hover:bg-red-500/20"}`}
                >
                  {selected.banned ? <><Check size={12} /> Unban</> : <><Ban size={12} /> Ban</>}
                </button>
                <button
                  data-testid="admin-user-toggle-creator"
                  onClick={() => updateUser({ is_creator: !selected.is_creator })}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white"
                >
                  {selected.is_creator ? "Revoke Creator" : "Grant Creator"}
                </button>
                <button
                  data-testid="admin-user-toggle-verified"
                  onClick={() => updateUser({ verified: !selected.verified })}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white"
                >
                  {selected.verified ? "Un-verify" : "Verify"}
                </button>
                <button
                  data-testid="admin-user-delete"
                  onClick={deleteUser}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md bg-red-500/10 text-red-300 hover:bg-red-500/20"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>

              {detail?.recent_posts?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Recent posts</div>
                  <ul className="space-y-1.5 text-xs">
                    {detail.recent_posts.map((p) => (
                      <li key={p.id} className={`text-white/70 ${p.hidden ? "opacity-50 line-through" : ""}`}>
                        <span className="text-[9px] uppercase tracking-widest text-white/40 mr-2">{p.kind}</span>
                        {(p.body || "(empty)").slice(0, 100)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Moderation Queue
   ============================================================ */
export function AdminModerationPanel() {
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { setQ(await adminDashboard.moderationQueue()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (item, status) => {
    try {
      await adminDashboard.resolveReport(item.kind, item.id, status);
      toast.success(`Report ${status}`);
      sfx.click();
      // Optimistic remove
      setQ((prev) => prev ? { ...prev, items: prev.items.filter((x) => x.id !== item.id) } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    }
  };

  const items = useMemo(() => {
    if (!q?.items) return [];
    if (filter === "all") return q.items;
    return q.items.filter((i) => i.kind === filter);
  }, [q, filter]);

  return (
    <div data-testid="admin-moderation">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Total Reports" value={q?.total ?? "—"} icon={Flag} />
        <StatCard label="Post Reports" value={q?.post_reports_open ?? "—"} icon={MessageSquare} accent="text-cyan-300" />
        <StatCard label="Event Reports" value={q?.event_reports_open ?? "—"} icon={Calendar} accent="text-orange-300" />
      </div>

      <div className="flex items-center gap-2 mb-3">
        {["all", "post", "event"].map((f) => (
          <button
            key={f}
            data-testid={`admin-mod-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${
              filter === f ? "bg-[#FFBF00] text-black font-bold" : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            {f === "all" ? "All" : f === "post" ? "Posts" : "Events"}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-xl glass border border-white/10 divide-y divide-white/5" data-testid="admin-mod-list">
        {items.length === 0 && !loading && <Empty>No open reports. 🎉</Empty>}
        {items.map((it) => (
          <div key={`${it.kind}-${it.id}`} className="p-3 flex items-start gap-3" data-testid={`admin-mod-item-${it.id}`}>
            <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${it.kind === "post" ? "bg-cyan-500/10 text-cyan-300" : "bg-orange-500/10 text-orange-300"}`}>
              {it.kind === "post" ? <MessageSquare size={14} /> : <Calendar size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest bg-white/5 text-white/60 px-1.5 py-0.5 rounded">{it.kind}</span>
                <span className="text-[10px] text-white/40">{(it.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="mt-1 text-sm text-white truncate">
                {it.kind === "post"
                  ? (it.target?.body || "(post body removed)")
                  : (it.target?.title || it.event_slug || "(unknown event)")}
              </div>
              <div className="text-xs text-red-300/80 mt-0.5">
                <AlertTriangle size={10} className="inline mr-1" />
                {it.reason || "no reason"}
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">
                Reporter: <code>{(it.reporter_id || "—").slice(0, 8)}</code>
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button
                data-testid={`admin-mod-resolve-${it.id}`}
                onClick={() => resolve(it, "resolved")}
                className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-green-500/10 text-green-300 hover:bg-green-500/20"
              >Resolve</button>
              <button
                data-testid={`admin-mod-dismiss-${it.id}`}
                onClick={() => resolve(it, "dismissed")}
                className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-white/5 text-white/60 hover:bg-white/10"
              >Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   YouTube Sync Viewer
   ============================================================ */
export function AdminYouTubePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});

  const load = async () => {
    setLoading(true);
    try { setData(await adminDashboard.youtube()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncOne = async (id) => {
    setSyncing((s) => ({ ...s, [id]: true }));
    try {
      const r = await adminDashboard.syncCreator(id);
      toast.success(`Synced (${r?.synced ?? 0} videos)`);
      sfx.click();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div data-testid="admin-youtube-viewer">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Worker" value={data?.worker_enabled ? "ON" : "OFF"} icon={Activity} accent={data?.worker_enabled ? "text-green-300" : "text-red-300"} />
        <StatCard label="Interval (sec)" value={data?.worker_interval_sec ?? "—"} icon={RefreshCw} />
        <StatCard label="Creators" value={data?.total_creators ?? "—"} icon={Users} />
        <StatCard label="Videos Synced" value={data?.total_videos ?? "—"} icon={Video} accent="text-red-300" />
      </div>

      <Section
        title="Creators"
        right={<button onClick={load} className="text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh</button>}
      >
        {(data?.creators || []).length === 0 && !loading && <Empty>No creators yet.</Empty>}
        <div className="divide-y divide-white/5">
          {(data?.creators || []).map((c) => (
            <div key={c.id} className="py-2 flex items-center gap-3" data-testid={`admin-yt-row-${c.id}`}>
              {c.avatar
                ? <img src={c.avatar} alt="" className="w-9 h-9 rounded-full object-cover bg-white/5" />
                : <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/40 text-xs">{(c.name || "?")[0]}</div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{c.name}</span>
                  {c.tier && <span className="text-[9px] uppercase tracking-widest text-[#FFBF00] bg-[#FFBF00]/10 px-1.5 py-0.5 rounded">{c.tier}</span>}
                </div>
                <div className="text-[10px] text-white/40 truncate">
                  {c.channel_title || c.handle || "no channel"} · {c.channel_id || "—"}
                </div>
              </div>
              <div className="text-right text-xs text-white/60 shrink-0">
                <div>{(c.subscribers || 0).toLocaleString()} subs</div>
                <div className="text-white/40">{c.videos} / {c.yt_video_count} vids</div>
                <div className="text-[10px] text-white/30 mt-0.5">
                  {c.last_sync_at ? `synced ${c.last_sync_at.slice(0, 16).replace("T", " ")}` : "never synced"}
                </div>
              </div>
              <button
                data-testid={`admin-yt-sync-${c.id}`}
                onClick={() => syncOne(c.id)}
                disabled={syncing[c.id]}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-md bg-[#FFBF00]/10 text-[#FFBF00] hover:bg-[#FFBF00]/20 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <RefreshCw size={11} className={syncing[c.id] ? "animate-spin" : ""} /> Sync
              </button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ============================================================
   Analytics Dashboard
   ============================================================ */
function BarChart({ data, label, color = "#FFBF00" }) {
  const max = Math.max(1, ...data.map((d) => d.v));
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">{label}</div>
      <div className="flex items-end gap-1 h-24">
        {data.map((d) => (
          <div key={d.d} className="flex-1 flex flex-col items-center justify-end group" title={`${d.d}: ${d.v}`}>
            <div
              className="w-full rounded-t transition-all"
              style={{ height: `${(d.v / max) * 100}%`, minHeight: 2, background: color, opacity: 0.35 + (d.v / max) * 0.65 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[9px] text-white/30">
        <span>{data[0]?.d?.slice(5)}</span>
        <span>{data[data.length - 1]?.d?.slice(5)}</span>
      </div>
    </div>
  );
}

export function AdminAnalyticsPanel() {
  const [snap, setSnap] = useState(null);
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([adminDashboard.analytics(), adminDashboard.analyticsTimeseries(14)]);
      setSnap(a);
      setTs(b);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-white/50">
          {snap?.generated_at ? `Snapshot @ ${snap.generated_at.slice(0, 16).replace("T", " ")} UTC` : "Loading…"}
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Users" value={snap?.users.total ?? "—"} icon={Users} />
        <StatCard label="Active Now" value={snap?.users.active_now ?? "—"} icon={Activity} accent="text-green-300" />
        <StatCard label="New 24h" value={snap?.users.new_24h ?? "—"} icon={TrendingUp} accent="text-cyan-300" />
        <StatCard label="Creators" value={snap?.users.creators ?? "—"} icon={Video} accent="text-pink-300" />
        <StatCard label="Posts" value={snap?.content.total_posts ?? "—"} icon={MessageSquare} />
        <StatCard label="Posts 24h" value={snap?.content.posts_24h ?? "—"} icon={TrendingUp} accent="text-cyan-300" />
        <StatCard label="Communities" value={snap?.content.total_communities ?? "—"} icon={Users} accent="text-purple-300" />
        <StatCard label="Videos" value={snap?.content.total_videos ?? "—"} icon={Video} accent="text-red-300" />
      </div>

      {ts && (
        <Section title="Last 14 days">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BarChart data={ts.posts} label="Posts / day" color="#FFBF00" />
            <BarChart data={ts.signups} label="New signups / day" color="#00E5FF" />
            <BarChart data={ts.searches} label="Searches / day" color="#FF66CC" />
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title="Top communities">
          {(snap?.top_communities || []).length === 0 && <Empty>No data.</Empty>}
          <ul className="space-y-1.5">
            {(snap?.top_communities || []).map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-sm text-white/80">
                {c.avatar ? <img src={c.avatar} alt="" className="w-6 h-6 rounded object-cover" /> : <div className="w-6 h-6 rounded bg-white/5" />}
                <Link to={`/communities/${c.slug}`} className="truncate hover:text-[#FFBF00]">{c.name}</Link>
                <span className="ml-auto text-[10px] text-white/40">{c.member_count} · {c.post_count}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Top creators">
          {(snap?.top_creators || []).length === 0 && <Empty>No data.</Empty>}
          <ul className="space-y-1.5">
            {(snap?.top_creators || []).map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-sm text-white/80">
                {c.avatar ? <img src={c.avatar} alt="" className="w-6 h-6 rounded-full object-cover" /> : <div className="w-6 h-6 rounded-full bg-white/5" />}
                <Link to={`/creators/${c.id}`} className="truncate hover:text-[#FFBF00]">{c.name}</Link>
                {c.tier && <span className="text-[9px] uppercase tracking-widest text-[#FFBF00]/70">{c.tier}</span>}
                <span className="ml-auto text-[10px] text-white/40">{(c.subscribers || 0).toLocaleString()} subs</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Top searches (7d)">
          {(snap?.top_searches || []).length === 0 && <Empty>No searches yet.</Empty>}
          <ul className="space-y-1.5">
            {(snap?.top_searches || []).map((s) => (
              <li key={s.q} className="flex items-center gap-2 text-sm text-white/80">
                <Search size={12} className="text-[#FFBF00]/70" />
                <Link to={`/search?q=${encodeURIComponent(s.q)}`} className="truncate hover:text-[#FFBF00]">{s.q}</Link>
                <span className="ml-auto text-[10px] text-white/40">{s.count}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
}

/* ============================================================
   Audit Log
   ============================================================ */
const AUDIT_ACTION_META = {
  "user.update": { color: "text-cyan-300", label: "User Updated" },
  "user.delete": { color: "text-red-300", label: "User Deleted" },
  "report.resolved": { color: "text-green-300", label: "Report Resolved" },
  "report.dismissed": { color: "text-white/60", label: "Report Dismissed" },
  "youtube.sync": { color: "text-pink-300", label: "YouTube Sync" },
};

export function AdminAuditLogPanel() {
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = actionFilter ? `?action=${encodeURIComponent(actionFilter)}&limit=200` : "?limit=200";
      const r = await authClient.get(`/admin/dashboard/audit-log${params}`, {
        headers: { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" },
      });
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [actionFilter]);
  useEffect(() => {
    authClient.get("/admin/dashboard/audit-log/actions", {
      headers: { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" },
    }).then((r) => setActions(r.data || [])).catch(() => setActions([]));
  }, []);

  return (
    <div data-testid="admin-audit-log">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          data-testid="audit-filter-all"
          onClick={() => setActionFilter("")}
          className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${
            actionFilter === "" ? "bg-[#FFBF00] text-black font-bold" : "bg-white/5 text-white/70 hover:bg-white/10"
          }`}
        >All Actions</button>
        {actions.map((a) => (
          <button
            key={a}
            data-testid={`audit-filter-${a}`}
            onClick={() => setActionFilter(a)}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${
              actionFilter === a ? "bg-[#FFBF00] text-black font-bold" : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >{a}</button>
        ))}
        <button onClick={load} className="ml-auto text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-2xl bg-[#141414] border-2 border-white/10 divide-y divide-white/5" data-testid="audit-log-list">
        {loading && <div className="p-6"><SkeletonList count={6} /></div>}
        {!loading && entries.length === 0 && (
          <div className="text-center text-white/40 text-sm py-12">
            <ClipboardList size={22} className="mx-auto mb-2 opacity-40" />
            No admin actions logged yet.
          </div>
        )}
        {!loading && entries.map((e) => {
          const M = AUDIT_ACTION_META[e.action] || { color: "text-white/70", label: e.action };
          return (
            <div key={e.id} className="p-3 flex items-start gap-3" data-testid={`audit-row-${e.id}`}>
              <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-white/5 ${M.color}`}>
                <FileText size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest ${M.color} font-bold`}>{M.label}</span>
                  <span className="text-[10px] text-white/40">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-sm text-white mt-0.5">
                  <span className="text-[#FFBF00]">{e.actor_name}</span>
                  <span className="text-white/40"> · {e.action}</span>
                  {e.target_kind && <span className="text-white/60"> → {e.target_kind}:<code className="text-white/50 ml-1">{(e.target_id || "").slice(0, 12)}</code></span>}
                </div>
                {e.meta && Object.keys(e.meta).length > 0 && (
                  <details className="mt-1">
                    <summary className="text-[10px] uppercase tracking-widest text-white/40 cursor-pointer hover:text-white/70">meta</summary>
                    <pre className="text-[10px] text-white/50 mt-1 whitespace-pre-wrap break-all font-mono">
                      {JSON.stringify(e.meta, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
